import { useDepartments, useDepartmentsSearchInline } from "@/queries/use-departments.query.ts";
import { useDivisionsByIds, useDivisionsSearchInline } from "@/queries/use-divisitions.query.ts";
import { useLegalEntities } from "@/queries/use-legal-entities.query.ts";
import { useLocations, useLocationsSearchInline } from "@/queries/use-locations.query.ts";
import {
  ApiField,
  ApiFieldReferenceEntity,
  ApiFieldValueTypes,
} from "@/types/custom-field.types.ts";
import { getEntityReferenceTitle } from "@/utils/entity-reference.utils.ts";
import { SelectItem } from "@skailer-com/uikit";
import { unionBy } from "lodash";
import { useMemo } from "react";
import { Control, FieldValues, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface EntityReferenceOption {
  id: number;
  title: string;
  is_archived: boolean;
  level?: number;
}

type DirectoryEntry = { id: number; title: string; is_archived?: boolean; level?: number };

const toOption = (entry: DirectoryEntry): EntityReferenceOption => ({
  id: entry.id,
  title: entry.title,
  is_archived: entry.is_archived ?? false,
  level: entry.level,
});

type ReferenceField = Pick<ApiField, "field_type" | "reference_entity" | "slug">;

const isReferenceTo = (field: ReferenceField, entity: ApiFieldReferenceEntity) =>
  field.field_type === ApiFieldValueTypes.entity_reference && field.reference_entity === entity;

const toIds = (value: unknown): number[] =>
  Array.isArray(value) ? value.filter((id): id is number => typeof id === "number") : [];

/**
 * Titles for previews. Legal entities, departments and locations load whole directories;
 * divisions are paginated, so only the ids present in `values` are resolved.
 */
export function useEntityReferenceOptions(
  fields: ReferenceField[],
  values: Record<string, unknown> = {}
) {
  const uses = (entity: ApiFieldReferenceEntity) =>
    fields.some((field) => isReferenceTo(field, entity));
  const divisionIds = [
    ...new Set(
      fields
        .filter((field) => isReferenceTo(field, ApiFieldReferenceEntity.division))
        .flatMap((field) => toIds(values[field.slug]))
    ),
  ].sort((a, b) => a - b);

  const legalEntities = useLegalEntities(uses(ApiFieldReferenceEntity.legal_entity));
  const departments = useDepartments(uses(ApiFieldReferenceEntity.department));
  const locations = useLocations(uses(ApiFieldReferenceEntity.location));
  const divisions = useDivisionsByIds({
    ids: divisionIds,
    enabled: uses(ApiFieldReferenceEntity.division),
  });

  const optionsByEntity = useMemo<Record<ApiFieldReferenceEntity, EntityReferenceOption[]>>(
    () => ({
      [ApiFieldReferenceEntity.legal_entity]: (legalEntities.data ?? []).map(toOption),
      [ApiFieldReferenceEntity.department]: (departments.data ?? []).map(toOption),
      [ApiFieldReferenceEntity.location]: (locations.data ?? []).map(toOption),
      [ApiFieldReferenceEntity.division]: (divisions.data ?? []).map(toOption),
    }),
    [legalEntities.data, departments.data, locations.data, divisions.data]
  );

  return {
    optionsByEntity,
    isLoading:
      legalEntities.isLoading ||
      departments.isLoading ||
      locations.isLoading ||
      divisions.isLoading,
  };
}

/** Watches only division reference fields: other directories need no form values for titles. */
export function useEntityReferenceValues(
  fields: ReferenceField[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- forms of any shape; names come from field.slug
  control: Control<any>
): Record<string, unknown> {
  const slugs = useMemo(
    () =>
      fields
        .filter((field) => isReferenceTo(field, ApiFieldReferenceEntity.division))
        .map((field) => field.slug),
    [fields]
  );
  const watched = useWatch({ control: control as Control<FieldValues>, name: slugs });
  return useMemo(
    () => Object.fromEntries(slugs.map((slug, index) => [slug, watched?.[index]])),
    [slugs, watched]
  );
}

export interface EntityReferenceSelectProps {
  options: SelectItem[];
  searchOptions?: SelectItem[];
  onSearchOptions?: (text: string | undefined) => void;
  loadOptionsOnOpen?: boolean;
  isLoading: boolean;
}

/**
 * Props for uikit selects, built on the same directory hooks as the system position fields:
 * departments keep the tree order and `level`, divisions search on the server.
 * Archived entries stay only when selected and are labelled.
 */
export function useEntityReferenceSelect(
  referenceEntity: ApiFieldReferenceEntity | null | undefined,
  selectedIds: number[],
  selectedReferences: DirectoryEntry[] = [],
  enabled = true
): EntityReferenceSelectProps {
  const { t } = useTranslation();
  const archivedLabel = t("custom_fields:reference_archived");
  const is = (entity: ApiFieldReferenceEntity) => enabled && referenceEntity === entity;

  const legalEntities = useLegalEntities(is(ApiFieldReferenceEntity.legal_entity));
  const departments = useDepartmentsSearchInline(is(ApiFieldReferenceEntity.department));
  const locations = useLocationsSearchInline(is(ApiFieldReferenceEntity.location));
  const divisions = useDivisionsSearchInline(is(ApiFieldReferenceEntity.division));
  const missingDivisionIds = selectedIds.filter(
    (id) => !selectedReferences.some((reference) => reference.id === id)
  );
  const selectedDivisions = useDivisionsByIds({
    ids: missingDivisionIds,
    enabled: is(ApiFieldReferenceEntity.division),
  });

  return useMemo(() => {
    const toItem = (entry: DirectoryEntry): SelectItem => ({
      id: entry.id,
      title: getEntityReferenceTitle(
        { title: entry.title, is_archived: entry.is_archived ?? false },
        archivedLabel
      ),
      ...(entry.level !== undefined ? { level: entry.level } : {}),
    });
    const visible = (entries: DirectoryEntry[] | undefined) =>
      (entries ?? [])
        .filter((entry) => !entry.is_archived || selectedIds.includes(entry.id))
        .map(toItem);
    const selected = selectedReferences.map(toItem);

    switch (referenceEntity) {
      case ApiFieldReferenceEntity.legal_entity:
        return {
          options: unionBy(selected, visible(legalEntities.data), "id"),
          isLoading: legalEntities.isLoading,
        };
      case ApiFieldReferenceEntity.department:
        return {
          options: unionBy(visible(departments.data), selected, "id"),
          searchOptions: visible(departments.options),
          onSearchOptions: departments.search,
          isLoading: departments.isLoading,
        };
      case ApiFieldReferenceEntity.location:
        return {
          options: unionBy(visible(locations.data), selected, "id"),
          searchOptions: visible(locations.options as DirectoryEntry[]),
          onSearchOptions: locations.search,
          isLoading: locations.isLoading,
        };
      case ApiFieldReferenceEntity.division:
        return {
          options: unionBy(selected, (selectedDivisions.data ?? []).map(toItem), "id"),
          searchOptions: visible(divisions.data?.items),
          onSearchOptions: divisions.search,
          loadOptionsOnOpen: true,
          isLoading: divisions.isLoading || selectedDivisions.isLoading,
        };
      default:
        return { options: [], isLoading: false };
    }
  }, [
    referenceEntity,
    selectedIds,
    selectedReferences,
    archivedLabel,
    legalEntities.data,
    legalEntities.isLoading,
    departments.data,
    departments.options,
    departments.search,
    departments.isLoading,
    locations.data,
    locations.options,
    locations.search,
    locations.isLoading,
    divisions.data,
    divisions.search,
    divisions.isLoading,
    selectedDivisions.data,
    selectedDivisions.isLoading,
  ]);
}
